import React from 'react';
import styled, { keyframes } from 'styled-components';

const Container = styled.div`
  width: 100%;
  /* [변경 사유]
   * - SVG 기반 차트는 높이가 너무 낮으면(세로 픽셀 수 부족) 곡선/마커가 뭉개져 보일 수 있습니다.
   * - 설계사 사용 화면(반복 조회)에서 피로도를 줄이기 위해, 기본 높이를 소폭 올려 가독성을 개선합니다.
   */
  height: 220px;
  position: relative;
  padding: 0;
`;

const SVG = styled.svg`
  overflow: visible;
`;

const Legend = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 10px;
  padding-left: 15px; /* 지표 범례 좌측 여백 추가 */
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  user-select: none;
  font-size: 0.85rem;
  color: #2d3748;
  opacity: ${props => (props.dim ? 0.35 : 1)};
`;

const Swatch = styled.div`
  width: 10px;
  height: 10px;
  border-radius: 999px;
  background: ${props => props.color};
`;

const _draw = keyframes`
  from {
    stroke-dashoffset: var(--dash);
  }
  to {
    stroke-dashoffset: 0;
  }
`;

// eslint-disable-next-line no-unused-vars
const SeriesPath = styled.path`
  /* [변경 사유]
   * - polyline(직선) 대신 path(곡선)를 사용하면 훨씬 부드럽고 자연스럽게 보입니다.
   * - 외부 차트 라이브러리 없이도 draw 애니메이션(stroke-dash)로 제품 같은 느낌을 보강합니다.
   */
  stroke-dasharray: var(--dash);
  stroke-dashoffset: var(--dash);
  animation: ${_draw} 650ms ease-out forwards;
  /* [변경 사유]
   * - preserveAspectRatio 설정에 따라 SVG가 리사이즈될 때 stroke가 함께 스케일되면
   *   선이 흐릿하거나 두께가 들쭉날쭉해 보일 수 있습니다.
   * - non-scaling-stroke로 선 두께를 픽셀 기준으로 고정해 시인성을 안정화합니다.
   */
  vector-effect: non-scaling-stroke;
`;

// eslint-disable-next-line no-unused-vars
const _buildSmoothPathD = (pts) => {
    // [변경 사유]
    // - 선이 딱딱하게 보이는 문제를 개선하기 위해, Catmull-Rom을 Bezier로 근사하여 곡선 path를 만듭니다.
    // - points 길이가 1이면 선을 그릴 수 없으므로 null을 반환합니다.
    if (!Array.isArray(pts) || pts.length < 2) return null;

    if (pts.length === 2) {
        return `M ${pts[0].x},${pts[0].y} L ${pts[1].x},${pts[1].y}`;
    }

    const d = [];
    d.push(`M ${pts[0].x},${pts[0].y}`);

    // Catmull-Rom to Bezier
    for (let i = 0; i < pts.length - 1; i += 1) {
        const p0 = pts[i - 1] || pts[i];
        const p1 = pts[i];
        const p2 = pts[i + 1];
        const p3 = pts[i + 2] || p2;

        // 장력(tension) 0.5에 해당하는 일반적인 근사
        const cp1x = p1.x + (p2.x - p0.x) / 6;
        const cp1y = p1.y + (p2.y - p0.y) / 6;
        const cp2x = p2.x - (p3.x - p1.x) / 6;
        const cp2y = p2.y - (p3.y - p1.y) / 6;

        d.push(`C ${cp1x},${cp1y} ${cp2x},${cp2y} ${p2.x},${p2.y}`);
    }

    return d.join(' ');
};

// eslint-disable-next-line no-unused-vars
const _buildStepPathD = (pts) => {
    // [변경 사유]
    // - Trend v2는 "수치"가 아니라 "정상/주의/위험"의 3단 카테고리(level) 변화를 보여주는 차트입니다.
    // - 이 데이터를 곡선으로 이어버리면(완만한 곡선) 상태가 서서히 변한 것처럼 오해될 수 있어
    //   미니멀 UX 관점에서도 부자연스럽게 보입니다.
    // - 따라서 상태가 바뀌는 시점만 또렷하게 보이도록 스텝(계단형) 라인을 사용합니다.
    if (!Array.isArray(pts) || pts.length < 2) return null;

    const d = [];
    d.push(`M ${pts[0].x},${pts[0].y}`);
    for (let i = 0; i < pts.length - 1; i += 1) {
        const p1 = pts[i];
        const p2 = pts[i + 1];
        // horizontal then vertical (step-after 스타일)
        d.push(`L ${p2.x},${p1.y}`);
        d.push(`L ${p2.x},${p2.y}`);
    }
    return d.join(' ');
};

const _fmtTrendDateYYMM = (v) => {
    // [변경 사유]
    // - Trend x축 날짜를 "YY.MM" 형태로 통일해 가독성을 높입니다.
    // - 백엔드/가공 로직에 따라 'YYYY-MM-DD', 'YYYY-MM', Date 객체 등 다양한 형태가 들어올 수 있어
    //   안전하게 파싱 후 포맷팅합니다.
    if (!v) return '';
    const s = typeof v === 'string' ? v : v instanceof Date ? v.toISOString().slice(0, 10) : String(v);

    // 1) 문자열에서 YYYY-MM(-DD) 패턴 우선 처리
    const m = s.match(/(\d{4})-(\d{2})/);
    if (m) {
        const yy = m[1].slice(2);
        const mm = m[2];
        return `${yy}.${mm}`;
    }

    // 2) Date 파싱 시도
    const d = new Date(s);
    if (!Number.isNaN(d.getTime())) {
        const yy = String(d.getFullYear()).slice(2);
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        return `${yy}.${mm}`;
    }

    return s;
};

const _getIndicatorSpec = (data) => {
    // [변경 사유]
    // - 지표별 정상/주의/위험 구간 판정을 위해 임계치 스펙이 필요합니다.
    // - 현재 health_trend(단일 시계열) 응답에는 임계치 정보가 없으므로,
    //   label/item명을 기반으로 프론트에서 임시 매핑합니다.
    // - 추후 health_trend_v2에서 bands(정상/주의/위험 기준)를 내려주면 해당 값으로 대체하는 것이 정답입니다.
    const label = (data?.[0]?.label || '').toLowerCase();

    // high_is_bad: 값이 높을수록 위험(예: 혈압/혈당/BMI 등)
    const specs = [
        { keys: ['sbp', 'bp', 'bloodpressure', 'systolic'], normalMax: 129, cautionMax: 139, highIsBad: true },
        { keys: ['glucose', 'bloodsugar'], normalMax: 99, cautionMax: 125, highIsBad: true },
        { keys: ['bmi'], normalMax: 22.9, cautionMax: 24.9, highIsBad: true },
        { keys: ['chol', 'cholesterol'], normalMax: 199, cautionMax: 239, highIsBad: true },
        { keys: ['alt'], normalMax: 40, cautionMax: 79, highIsBad: true },
        { keys: ['creat', 'creatinine'], normalMax: 1.2, cautionMax: 1.5, highIsBad: true },
    ];

    // [변경 사유]
    // - 현재 백엔드의 하위호환 health_trend는 "공복혈당"을 우선 제공하지만,
    //   label 필드가 내려오지 않아 프론트에서 지표 매칭이 실패할 수 있습니다.
    // - 이 경우 기본 지표를 공복혈당으로 간주해 임계치 기반 밴드/구간표시가 항상 동작하도록 합니다.
    if (!label) {
        const glucose = specs.find(s => s.keys.includes('glucose'));
        if (glucose) {
            return {
                normalMax: glucose.normalMax,
                cautionMax: glucose.cautionMax,
                highIsBad: glucose.highIsBad,
            };
        }
    }

    const hit = specs.find(s => s.keys.some(k => label.includes(k)));
    if (!hit) {
        // [변경 사유]
        // - label 매칭이 실패하더라도, 현재 화면의 Trend는 대부분 공복혈당으로 내려오므로
        //   공복혈당 기준으로 폴백합니다.
        const glucose = specs.find(s => s.keys.includes('glucose'));
        if (!glucose) return null;
        return {
            normalMax: glucose.normalMax,
            cautionMax: glucose.cautionMax,
            highIsBad: glucose.highIsBad,
        };
    }
    return {
        normalMax: hit.normalMax,
        cautionMax: hit.cautionMax,
        highIsBad: hit.highIsBad,
    };
};

const _levelBySpec = (value, spec) => {
    if (!spec || value === null || value === undefined) return null;
    const v = Number(value);
    if (Number.isNaN(v)) return null;

    // [주의]
    // - 현재는 "highIsBad" 케이스(값이 높을수록 위험) 위주로 매핑합니다.
    // - lowIsBad(예: HDL 등)는 추후 확장 시 분기 추가가 필요합니다.
    if (spec.highIsBad) {
        if (v <= spec.normalMax) return 'normal';
        if (v <= spec.cautionMax) return 'caution';
        return 'danger';
    }

    // 기본값(예외 케이스) 처리
    if (v >= spec.normalMax) return 'normal';
    if (v >= spec.cautionMax) return 'caution';
    return 'danger';
};

const HealthTrendChart = ({ data = [], trendV2 }) => {
    // [변경 사유]
    // - health_trend_v2(6개 지표 + bands/points)를 우선 사용해, 6개 지표를 한 화면에 표현합니다.
    // - 수치(value)는 민감정보로 간주되어 화면에 출력하지 않습니다.
    // - health_trend(구버전 단일 배열)는 하위호환 폴백으로만 사용합니다.

    const v2Indicators = trendV2?.indicators || [];
    const hasV2 = Array.isArray(v2Indicators) && v2Indicators.length > 0;

    const [hoverKey, setHoverKey] = React.useState(null);
    const [activeKey, setActiveKey] = React.useState(null);

    // [변경 사유]
    // - 기존 팔레트가 다소 원색/고채도로 보여 시각적 노이즈가 커질 수 있어,
    //   뮤트(Muted) 톤으로 조정합니다.
    // - hover/클릭 고정 시 강조 대비가 더 자연스럽게 보이도록 설계합니다.
    // 색상 팔레트(지표별 고정)
    const colorByKey = {
        bp_sys: '#4c6fff',
        glucose: '#22c55e',
        bmi: '#8b5cf6',
        cholesterol: '#f59e0b',
        alt: '#f97316',
        creatinine: '#14b8a6',
    };

    const width = 500; // viewBox width
    const height = 150; // viewBox height
    const safePad = 2;

    // [변경 사유]
    // - 좌측 라벨(정상/주의/위험)과 차트 선이 겹치지 않도록,
    //   플롯 영역을 좌측 30px/우측 20px 만큼 안쪽으로 이동합니다.
    // - 추가로, 좌측 첫 인덱스(시작점)가 "정상" 글자 시작 지점보다 더 오른쪽에 위치하도록
    //   좌측 패딩을 소폭 늘려 시각적 여백을 확보합니다.
    // - 배경 밴드/점선 경계도 플롯 영역에 맞춰 함께 조정합니다.
    const plotPadLeft = 42;
    const plotPadRight = 20;
    const plotX0 = plotPadLeft;
    const plotW = width - plotPadLeft - plotPadRight;

    // 3구간(위험/주의/정상) 중앙 y좌표 (카테고리 차트)
    const bandH = (height - safePad * 2) / 3;
    const bandMidY = {
        danger: safePad + bandH * 0.5,
        caution: safePad + bandH * 1.5,
        normal: safePad + bandH * 2.5,
    };

    // [변경 사유]
    // - 설계사들이 반복해서 확인하는 화면이므로, 한 화면에 6개 라인이 동시에 노출되면 시각적 피로도가 큽니다.
    // - "미니멀/모던" 스타일을 위해 기본 상태에서는 1개 지표만 표시하고(선택된 지표),
    //   나머지는 범례(토글)로만 제공하는 구조로 단순화합니다.
    const focusKey = hoverKey || activeKey;

    const _toggleActiveKey = (key) => {
        setActiveKey((prev) => (prev === key ? null : key));
    };

    const _fmtLevelKo = (lv) => (lv === 'danger' ? '위험' : lv === 'caution' ? '주의' : '정상');

    const _getAllDates = () => {
        const set = new Set();
        v2Indicators.forEach((ind) => {
            (ind?.points || []).forEach((p) => {
                if (p?.date) set.add(p.date);
            });
        });
        return Array.from(set).sort();
    };

    const _getXByDate = (dates) => {
        const n = dates.length;
        const map = {};
        dates.forEach((d, i) => {
            // [변경 사유]
            // - 사용자가 요청한 "하단 인덱스(날짜)" 이동은 라벨만이 아니라,
            //   해당 날짜의 실제 x좌표(선/점)까지 같이 움직여야 합니다.
            // - 따라서 xByDate 계산 단계에서 시작/끝을 안쪽으로 밀어(인셋) 라벨/선/점이 함께 이동하도록 합니다.
            // - 시작점은 +10~20px, 마지막은 -20px 요구를 반영해 인셋 값을 적용합니다.
            const startInset = 14;
            const endInset = 20;
            if (n <= 1) {
                map[d] = plotX0 + plotW / 2;
            } else {
                const innerW = Math.max(1, plotW - startInset - endInset);
                map[d] = plotX0 + startInset + (i / (n - 1)) * innerW;
            }
        });
        return map;
    };

    if (hasV2) {
        const dates = _getAllDates();
        if (dates.length === 0) return <div>데이터 없음</div>;
        const xByDate = _getXByDate(dates);

        // 각 지표별 polyline points 생성(날짜 순)
        const series = v2Indicators
            .map((ind) => {
                const key = ind?.key;
                const label = ind?.label || key;
                const pts = (ind?.points || [])
                    .filter((p) => p?.date && p?.level)
                    .sort((a, b) => String(a.date).localeCompare(String(b.date)))
                    .map((p) => ({
                        date: p.date,
                        level: p.level,
                        x: xByDate[p.date],
                        y: bandMidY[p.level],
                    }));

                return {
                    key,
                    label,
                    color: colorByKey[key] || '#3182ce',
                    points: pts,
                };
            })
            .filter((s) => s.key && s.points.length > 0);

        // [변경 사유]
        // - 사용자 요청: 6개 지표를 모두 동시에 표시합니다.
        // - 다만 화면 복잡도를 줄이기 위해 hover/클릭 고정된 지표만 강조하고, 나머지는 디밍 처리합니다.
        const seriesToRender = series;

        return (
            <div>
                <Container>
                    {/* [변경 사유]
                     * - preserveAspectRatio="none"은 컨테이너 크기에 맞춰 SVG를 비율 무시로 강제 늘려
                     *   텍스트/선이 흐릿하게 보이거나(스케일링) 왜곡될 수 있습니다.
                     * - meet로 비율을 유지하고, vector-effect(non-scaling-stroke)와 함께 사용해
                     *   선/마커가 또렷하게 보이도록 합니다.
                     */}
                    <SVG
                        viewBox={`0 0 ${width} ${height}`}
                        preserveAspectRatio="xMidYMid meet"
                        style={{ width: '100%', height: '100%', shapeRendering: 'geometricPrecision' }}
                    >
                        {/* Background Bands (위험/주의/정상) */}
                        {/* [변경 사유]
                         * - "미니멀" 톤을 위해 밴드는 거의 느껴지지 않을 정도로만 남깁니다.
                         * - 구간 인지는 점선 경계 + 좌측 라벨로 충분히 전달하고, 데이터 라인 가독성을 최우선합니다.
                         */}
                        <rect x={plotX0} y={safePad} width={plotW} height={bandH} fill="#ef4444" opacity="0.04" />
                        <rect x={plotX0} y={safePad + bandH} width={plotW} height={bandH} fill="#f59e0b" opacity="0.035" />
                        <rect x={plotX0} y={safePad + bandH * 2} width={plotW} height={bandH} fill="#22c55e" opacity="0.035" />

                        {/* Dashed Boundaries */}
                        {/* [변경 사유] 점선을 더 촘촘하게(짧은 대시/간격) 조정해 더 정돈된 느낌을 줍니다. */}
                        <line x1={plotX0} y1={safePad + bandH} x2={plotX0 + plotW} y2={safePad + bandH} stroke="#e2e8f0" strokeWidth="1" strokeDasharray="2 3" />
                        <line x1={plotX0} y1={safePad + bandH * 2} x2={plotX0 + plotW} y2={safePad + bandH * 2} stroke="#e2e8f0" strokeWidth="1" strokeDasharray="2 3" />

                        {/* Band Labels */}
                        {/* [변경 사유] 라벨이 선/포인트와 겹치지 않도록 각 밴드 상단 쪽으로 배치합니다. */}
                        <text x={8} y={safePad + 12} fontSize="10" fill="#c53030" fontWeight="700">{_fmtLevelKo('danger')}</text>
                        <text x={8} y={safePad + bandH + 12} fontSize="10" fill="#975a16" fontWeight="700">{_fmtLevelKo('caution')}</text>
                        <text x={8} y={safePad + bandH * 2 + 12} fontSize="10" fill="#276749" fontWeight="700">{_fmtLevelKo('normal')}</text>

                        {/* X축 날짜 */}
                        {dates.map((d, i) => (
                            <text key={i} x={xByDate[d]} y={height - 5} textAnchor="middle" fontSize="10" fill="#718096">
                                {_fmtTrendDateYYMM(d)}
                            </text>
                        ))}

                        {/* Series */}
                        {/* [변경 사유]
                          * - 사용자 요청: 스텝형/곡선이 아닌 "직선(폴리라인)" 연결 방식으로 표시합니다.
                          * - hover/클릭(고정)된 지표만 강조하고 나머지는 흐리게 처리해, 6개 동시 표시에서도 가독성을 확보합니다.
                          */}
                        {seriesToRender.map((s) => {
                            const dim = focusKey && focusKey !== s.key;
                            const strokeW = focusKey === s.key ? 3.0 : 2.25;
                            const opacity = dim ? 0.22 : 0.98;

                            return (
                                <g
                                    key={s.key}
                                    onMouseEnter={() => setHoverKey(s.key)}
                                    onMouseLeave={() => setHoverKey(null)}
                                >
                                    <polyline
                                        fill="none"
                                        stroke={s.color}
                                        strokeWidth={strokeW}
                                        points={s.points.map((p) => `${p.x},${p.y}`).join(' ')}
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        opacity={opacity}
                                        vectorEffect="non-scaling-stroke"
                                    />
                                    {s.points.map((p, idx) => (
                                        <circle
                                            key={idx}
                                            cx={p.x}
                                            cy={p.y}
                                            r={focusKey === s.key ? 4.0 : 3.0}
                                            fill="#ffffff"
                                            stroke={s.color}
                                            strokeWidth={2.0}
                                            opacity={opacity}
                                            vectorEffect="non-scaling-stroke"
                                            style={{ filter: 'none' }}
                                        />
                                    ))}
                                </g>
                            );
                        })}
                    </SVG>
                </Container>

                <Legend>
                    {series.map((s) => {
                        const dim = focusKey && focusKey !== s.key;
                        const isActive = activeKey === s.key;
                        return (
                            <LegendItem
                                key={s.key}
                                dim={dim}
                                onMouseEnter={() => setHoverKey(s.key)}
                                onMouseLeave={() => setHoverKey(null)}
                                onClick={() => _toggleActiveKey(s.key)}
                                title={isActive ? '클릭 고정 해제' : '클릭 고정'}
                                style={{ fontWeight: isActive ? 700 : 500 }}
                            >
                                <Swatch color={s.color} />
                                <span>{s.label}{isActive ? ' (고정)' : ''}</span>
                            </LegendItem>
                        );
                    })}
                </Legend>
            </div>
        );
    }

    // --- 하위호환(구버전 단일 지표) ---
    // data: [{ date: '2023-01', value: 120, label: 'sBP' }, ...]
    if (!data || data.length === 0) return <div>데이터 없음</div>;

    // [주의]
    // - 구버전 폴백에서는 "값"을 노출하지 않습니다.

    // Find min/max for scaling
    const values = data.map(d => d.value);
    const minVal = Math.min(...values) * 0.9;
    const maxVal = Math.max(...values) * 1.1;
    const range = maxVal - minVal || 1;

    const getX = (index) => safePad + (index / (data.length - 1)) * (width - 2 * safePad);
    const getY = (value) => height - safePad - ((value - minVal) / range) * (height - 2 * safePad);

    const points = data.map((d, i) => `${getX(i)},${getY(d.value)}`).join(' ');

    const spec = _getIndicatorSpec(data);
    const latestValue = data?.length ? data[data.length - 1]?.value : null;
    const level = _levelBySpec(latestValue, spec);
    const levelLabel = level === 'normal' ? '정상' : level === 'caution' ? '주의' : level === 'danger' ? '위험' : '';
    const levelColor = level === 'normal' ? '#276749' : level === 'caution' ? '#975a16' : level === 'danger' ? '#c53030' : '#4a5568';
    const levelBg = level === 'normal' ? '#f0fff4' : level === 'caution' ? '#fffff0' : level === 'danger' ? '#fff5f5' : '#f7fafc';

    // [변경 사유]
    // - "정상/주의/위험" 밴드를 단순 3등분이 아니라, 임계치(실제 기준값) 위치로 그릴 수 있도록
    //   밴드 경계(y좌표)를 계산합니다.
    // - spec 매칭 실패 시(임계치 없음) 기존 3등분 밴드로 fallback 합니다.
    const plotTop = safePad;
    const plotBottom = height - safePad;
    const plotH = plotBottom - plotTop;

    const _clamp = (v, min, max) => Math.max(min, Math.min(max, v));

    const _getBandBounds = () => {
        // 반환 형태: { dangerTop, dangerBottom, cautionTop, cautionBottom, normalTop, normalBottom }
        // 좌표계 기준: y가 작을수록 위쪽(큰 값 영역일 수도/작은 값 영역일 수도 있음)

        // 1) 임계치 기반 밴드
        if (spec && typeof spec.normalMax === 'number' && typeof spec.cautionMax === 'number') {
            // highIsBad: 값이 높을수록 위험(예: 혈압/혈당/BMI 등)
            if (spec.highIsBad) {
                const yCaution = _clamp(getY(spec.cautionMax), plotTop, plotBottom);
                const yNormal = _clamp(getY(spec.normalMax), plotTop, plotBottom);
                const top = plotTop;
                const bottom = plotBottom;

                return {
                    dangerTop: top,
                    dangerBottom: yCaution,
                    cautionTop: yCaution,
                    cautionBottom: yNormal,
                    normalTop: yNormal,
                    normalBottom: bottom,
                };
            }

            // lowIsBad(확장 대비): 값이 낮을수록 위험
            // danger: bottom~yCaution / caution: yCaution~yNormal / normal: top~yNormal
            const yCaution = _clamp(getY(spec.cautionMax), plotTop, plotBottom);
            const yNormal = _clamp(getY(spec.normalMax), plotTop, plotBottom);
            const top = plotTop;
            const bottom = plotBottom;

            return {
                dangerTop: yCaution,
                dangerBottom: bottom,
                cautionTop: yNormal,
                cautionBottom: yCaution,
                normalTop: top,
                normalBottom: yNormal,
            };
        }

        // 2) Fallback: 동일 높이 3등분
        const bandH = plotH / 3;
        return {
            dangerTop: plotTop,
            dangerBottom: plotTop + bandH,
            cautionTop: plotTop + bandH,
            cautionBottom: plotTop + bandH * 2,
            normalTop: plotTop + bandH * 2,
            normalBottom: plotBottom,
        };
    };

    const bands = _getBandBounds();

    // [변경 사유]
    // - 사용자의 "현재 수치"가 어느 구간(정상/주의/위험)에 속하는지 한눈에 보이도록
    //   해당 구간의 중앙에 마커(라벨)를 표시합니다.
    // - 밴드가 임계치 기반으로 바뀌더라도 중앙 표시가 깨지지 않도록, 실제 밴드 경계값으로 계산합니다.
    const bandMidYByLevel = (lv) => {
        if (lv === 'danger') return (bands.dangerTop + bands.dangerBottom) / 2;
        if (lv === 'caution') return (bands.cautionTop + bands.cautionBottom) / 2;
        return (bands.normalTop + bands.normalBottom) / 2;
    };

    return (
        <Container>
            <SVG viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" style={{ width: '100%', height: '100%' }}>
                {/* Background Bands (Danger / Caution / Normal) */}
                <rect x={0} y={bands.dangerTop} width={width} height={Math.max(0, bands.dangerBottom - bands.dangerTop)} fill="#fff5f5" />
                <rect x={0} y={bands.cautionTop} width={width} height={Math.max(0, bands.cautionBottom - bands.cautionTop)} fill="#fffff0" />
                <rect x={0} y={bands.normalTop} width={width} height={Math.max(0, bands.normalBottom - bands.normalTop)} fill="#f0fff4" />

                {/* Current Level Marker */}
                {level ? (
                    <g>
                        <line x1={safePad} y1={bandMidYByLevel(level)} x2={width - safePad} y2={bandMidYByLevel(level)} stroke={levelColor} strokeWidth="1" strokeDasharray="4 4" opacity="0.8" />
                        <rect
                            x={width / 2 - 48}
                            y={bandMidYByLevel(level) - 10}
                            width={96}
                            height={20}
                            rx={10}
                            fill={levelBg}
                            stroke={levelColor}
                            strokeWidth="1"
                        />
                        <text x={width / 2} y={bandMidYByLevel(level) + 4} textAnchor="middle" fontSize="10" fill={levelColor} fontWeight="700">
                            {`현재: ${levelLabel}`}
                        </text>
                    </g>
                ) : null}

                {/* Background Grid (Simplified) */}
                <line x1={safePad} y1={height - safePad} x2={width - safePad} y2={height - safePad} stroke="#e2e8f0" strokeWidth="1" />
                <line x1={safePad} y1={safePad} x2={safePad} y2={height - safePad} stroke="#e2e8f0" strokeWidth="1" />

                {/* Line */}
                <polyline
                    fill="none"
                    stroke="#3182ce"
                    strokeWidth="3"
                    points={points}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />

                {/* Data Points */}
                {data.map((d, i) => (
                    <g key={i}>
                        <circle cx={getX(i)} cy={getY(d.value)} r="4" fill="white" stroke="#3182ce" strokeWidth="2" />
                        <text x={getX(i)} y={height - 5} textAnchor="middle" fontSize="10" fill="#718096">
                            {_fmtTrendDateYYMM(d.date)}
                        </text>
                    </g>
                ))}
            </SVG>
        </Container>
    );
};

export default HealthTrendChart;
